import {
  RECORD_KINDS,
  type BootstrapResponse,
  type CurrentUser,
  type LoginRequest,
  type RecordDto,
  type RecordKind,
  type RecordOperation,
  type RecordOperationResult,
} from '@halo/shared';
import { create } from 'zustand';
import { authApi, bootstrapApi, recordsApi } from '@/api/endpoints';
import { runSessionResets } from './session-reset';

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated';

/** Dữ liệu nạp một lần sau đăng nhập. `user` tách riêng vì có ngay từ câu trả lời đăng nhập. */
export type WorkspaceData = Omit<BootstrapResponse, 'user'>;

interface SessionState {
  status: SessionStatus;
  user: CurrentUser | null;
  data: WorkspaceData | null;
  /** Tăng mỗi lần đăng xuất — câu trả lời về muộn của phiên trước bị vứt, không lẫn sang phiên sau. */
  generation: number;
  /** Tăng mỗi lần vá dữ liệu sau một lượt ghi — để `reload` nhận ra ảnh chụp của nó đã cũ. */
  writes: number;
  /** Thời điểm (ms) dữ liệu được nạp trọn vẹn từ máy chủ lần gần nhất. */
  loadedAt: number;

  initialize: () => Promise<void>;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  /** Phiên hết hạn ở máy chủ: dọn trạng thái, không gọi API. */
  expire: () => void;
  /** Nạp lại toàn bộ dữ liệu từ máy chủ (người khác có thể đã sửa). */
  reload: () => Promise<void>;
  /**
   * Gửi một lô thao tác rồi vá trạng thái cục bộ theo ĐÚNG những gì máy chủ đã nhận. Thao tác
   * bị từ chối không đụng tới trạng thái; người gọi đọc `results` để báo cho người dùng.
   */
  applyRecordOperations: (operations: RecordOperation[]) => Promise<RecordOperationResult[]>;
}

function splitBootstrap(bootstrap: BootstrapResponse): {
  user: CurrentUser;
  data: WorkspaceData;
  loadedAt: number;
} {
  const { user, ...data } = bootstrap;
  return { user, data, loadedAt: Date.now() };
}

export const useSessionStore = create<SessionState>((set, get) => ({
  status: 'loading',
  user: null,
  data: null,
  generation: 0,
  writes: 0,
  loadedAt: 0,

  async initialize() {
    const generation = get().generation;
    try {
      const { user } = await authApi.me();
      if (get().generation !== generation) return;
      if (!user) {
        set({ status: 'anonymous', user: null, data: null });
        return;
      }
      const bootstrap = await bootstrapApi.load();
      if (get().generation !== generation) return;
      set({ status: 'authenticated', ...splitBootstrap(bootstrap) });
    } catch {
      if (get().generation === generation) set({ status: 'anonymous', user: null, data: null });
    }
  },

  async login(credentials) {
    await authApi.login(credentials);
    const bootstrap = await bootstrapApi.load();
    set({ status: 'authenticated', ...splitBootstrap(bootstrap) });
  },

  async logout() {
    try {
      await authApi.logout();
    } finally {
      get().expire();
    }
  },

  expire() {
    runSessionResets();
    set((state) => ({
      status: 'anonymous',
      user: null,
      data: null,
      generation: state.generation + 1,
    }));
  },

  async reload() {
    // Tối đa hai lượt: lượt đầu bị một thao tác ghi chen ngang thì ảnh chụp vừa tải có thể được chụp
    // TRƯỚC lượt ghi đó — đem đè lên là chỗ người dùng vừa sửa «nhảy lùi» về giá trị cũ.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const { generation, writes } = get();
      const bootstrap = await bootstrapApi.load();
      if (get().generation !== generation) return;
      if (get().writes !== writes) continue;
      set({ ...splitBootstrap(bootstrap) });
      return;
    }
  },

  async applyRecordOperations(operations) {
    const generation = get().generation;
    const { results } = await recordsApi.batch(operations);
    const { data, user } = get();
    if (!data || !user || get().generation !== generation) return results;

    const records = { ...data.records };
    const touched = new Set<RecordKind>();
    const listOf = (kind: RecordKind): RecordDto[] => {
      if (!touched.has(kind)) {
        records[kind] = [...records[kind]];
        touched.add(kind);
      }
      return records[kind];
    };

    let needsReload = false;
    for (const result of results) {
      const operation = operations[result.index];
      if (!operation || result.status === 'rejected') continue;
      const list = listOf(operation.kind);
      const index = list.findIndex((record) => record.id === result.id);
      if (result.status === 'deleted' || result.status === 'not_found') {
        if (index >= 0) list.splice(index, 1);
        continue;
      }
      if (operation.op !== 'put') continue;
      // Đổi ảnh thì đường dẫn ảnh (mang hash nội dung) do máy chủ tính — nạp lại cho chắc.
      if (operation.images && Object.keys(operation.images).length) needsReload = true;
      if (index >= 0) {
        const current = list[index];
        // Khớp với máy chủ: bản ghi cài đặt được TRỘN (chỉ gửi khoá đổi), mọi loại khác thay trọn.
        // Thay trọn ở đây là vừa đổi một ngưỡng xong, danh sách CMS / đầu mối CID biến mất khỏi màn
        // hình cho tới lần tải lại.
        const data =
          operation.kind === 'setting' ? { ...current?.data, ...operation.data } : operation.data;
        // Sửa kèm `owner` = chuyển chủ (máy chủ đã nhận, không thì đã từ chối) — vá luôn chủ mới.
        const owner =
          RECORD_KINDS[operation.kind].owned && operation.owner
            ? operation.owner
            : (current?.owner ?? null);
        if (current) list[index] = { ...current, owner, data };
        if (current && owner !== current.owner) needsReload = true;
      } else {
        // Khớp với máy chủ: mục dùng chung (thư mục, cài đặt…) KHÔNG có chủ; gán tên mình vào là
        // bộ lọc xem theo người sẽ giấu/hiện sai bản ghi cho tới lần tải lại.
        const owned = RECORD_KINDS[operation.kind].owned;
        list.push({
          id: result.id,
          owner: owned ? (operation.owner ?? user.username) : null,
          data: operation.data,
          images: {},
        });
        // Người tạo thay cho người khác có thể bị máy chủ rơi về chính mình → lấy lại sự thật.
        if (owned && operation.owner && operation.owner !== user.username) needsReload = true;
      }
    }
    set((state) => ({ data: { ...data, records }, writes: state.writes + 1 }));
    if (needsReload) void get().reload();
    return results;
  },
}));

/** Chỉ dùng bên trong khu vực đã đăng nhập — ở đó `user` và `data` chắc chắn có. */
export function useCurrentUser(): CurrentUser {
  const user = useSessionStore((state) => state.user);
  if (!user) throw new Error('useCurrentUser() dùng ngoài khu vực đã đăng nhập');
  return user;
}

export function useWorkspace(): WorkspaceData {
  const data = useSessionStore((state) => state.data);
  if (!data) throw new Error('useWorkspace() dùng ngoài khu vực đã đăng nhập');
  return data;
}
